import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { GoogleMapsLoaderService } from '../../common/services/google-maps-loader.service';

interface OverlayPosition {
  parent: HTMLElement;
  nextSibling: Node | null;
}

@Component({
  selector: 'app-pure-map',
  templateUrl: './pure-map.component.html',
  styleUrls: ['./pure-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PureMapComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapDiv') mapDiv: ElementRef;
  map: google.maps.Map;

  @Input() height = 500;
  @Input() tilt = 45;
  @Output() mapInitialized = new EventEmitter<google.maps.Map>();

  private readonly fullscreenOverlays = new Set<HTMLElement>();
  private readonly overlayPositions = new Map<HTMLElement, OverlayPosition>();
  private isDestroyed = false;

  constructor(
    private googleMapsLoaderService: GoogleMapsLoaderService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  async ngAfterViewInit() {
    const map = await this.googleMapsLoaderService.initMap(this.mapDiv, this.tilt);
    if (this.isDestroyed) {
      return;
    }

    this.map = map;
    this.document.addEventListener('fullscreenchange', this.fullscreenChangeListener);
    this.mapInitialized.emit(this.map);
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.document.removeEventListener('fullscreenchange', this.fullscreenChangeListener);
    this.restoreFullscreenOverlays();
  }

  public registerFullscreenOverlay(overlay: HTMLElement): void {
    this.fullscreenOverlays.add(overlay);
  }

  public unregisterFullscreenOverlay(overlay: HTMLElement): void {
    this.restoreFullscreenOverlay(overlay);
    this.fullscreenOverlays.delete(overlay);
  }

  private readonly fullscreenChangeListener = (): void => {
    const fullscreenElement = this.document.fullscreenElement as HTMLElement | null;

    if (fullscreenElement && this.isMapFullscreenElement(fullscreenElement)) {
      this.moveOverlaysToFullscreen(fullscreenElement);
      return;
    }

    this.restoreFullscreenOverlays();
  };

  private moveOverlaysToFullscreen(fullscreenElement: HTMLElement): void {
    this.fullscreenOverlays.forEach(overlay => {
      if (!this.overlayPositions.has(overlay) && overlay.parentElement) {
        this.overlayPositions.set(overlay, {
          parent: overlay.parentElement,
          nextSibling: overlay.nextSibling
        });
      }

      if (overlay.parentElement !== fullscreenElement) {
        fullscreenElement.appendChild(overlay);
      }
    });
  }

  private isMapFullscreenElement(fullscreenElement: HTMLElement): boolean {
    const mapElement = this.mapDiv.nativeElement as HTMLElement;
    return fullscreenElement === mapElement || fullscreenElement.contains(mapElement) || mapElement.contains(fullscreenElement);
  }

  private restoreFullscreenOverlays(): void {
    [...this.overlayPositions.keys()].reverse().forEach(overlay => this.restoreFullscreenOverlay(overlay));
  }

  private restoreFullscreenOverlay(overlay: HTMLElement): void {
    const position = this.overlayPositions.get(overlay);
    if (!position) {
      return;
    }

    const referenceNode = position.nextSibling?.parentNode === position.parent ? position.nextSibling : null;
    position.parent.insertBefore(overlay, referenceNode);
    this.overlayPositions.delete(overlay);
  }
}
